/**
 * Installation de l'application sur l'écran d'accueil.
 *
 * Sur iPhone, le push web n'existe que pour un site ajouté à l'écran d'accueil
 * (Safari 16.4+). C'est la raison d'insister : sans cette étape, les
 * interrupteurs de notification du compte ne peuvent rien pousser. Android
 * accepte le push dans Chrome, mais l'icône reste le moyen de le recevoir
 * navigateur fermé.
 *
 * iPadOS se déclare « Macintosh » dans l'agent : sans le test tactile, on
 * prendrait une tablette pour un ordinateur et on cacherait la bannière à
 * ceux qui en ont le plus besoin.
 */

export type ClientPlatform = 'ios' | 'android' | 'other';

export interface InstallEnvironment {
  readonly userAgent: string;
  readonly platform: string;
  readonly maxTouchPoints: number;
  readonly iosStandalone: boolean;
  readonly displayModeStandalone: boolean;
  readonly coarsePointer: boolean;
  readonly narrowViewport: boolean;
}

export interface InstallOffer {
  readonly visible: boolean;
  readonly platform: ClientPlatform;
  readonly installed: boolean;
}

/** Refus de la bannière, le temps de la visite (`sessionStorage`). */
export const INSTALL_BANNER_STORAGE_KEY = 'champtable.install-banner';

const IPHONE = /iPhone|iPod/;
const IPAD = /iPad/;
const ANDROID = /Android/;

export function detectPlatform(env: InstallEnvironment): ClientPlatform {
  if (IPHONE.test(env.userAgent) || IPAD.test(env.userAgent)) {
    return 'ios';
  }
  // iPadOS 13+ : Safari se fait passer pour un Mac. Le nombre de points de
  // contact distingue la tablette du vrai ordinateur.
  if (env.platform === 'MacIntel' && env.maxTouchPoints > 1) {
    return 'ios';
  }
  if (ANDROID.test(env.userAgent)) {
    return 'android';
  }
  return 'other';
}

/**
 * Lancé depuis l'icône (PWA / raccourci) plutôt que depuis un onglet Safari
 * ou Chrome. `display-mode: standalone` couvre Android et les Chromium ;
 * `navigator.standalone` est l'indicateur historique d'iOS.
 */
export function isLaunchedFromShortcut(env: InstallEnvironment): boolean {
  return env.displayModeStandalone || env.iosStandalone;
}

export function isDismissed(storage: Pick<Storage, 'getItem'>): boolean {
  return storage.getItem(INSTALL_BANNER_STORAGE_KEY) === '1';
}

/**
 * Téléphone dans le navigateur, pas encore installé, bannière non masquée.
 * Un agent inconnu n'est proposé que s'il ressemble à un mobile (pointeur
 * grossier et écran étroit) : on affiche alors les deux recettes.
 */
export function shouldOfferInstall(env: InstallEnvironment, dismissed: boolean): boolean {
  if (dismissed || isLaunchedFromShortcut(env)) {
    return false;
  }
  const platform = detectPlatform(env);
  if (platform === 'ios' || platform === 'android') {
    return true;
  }
  return env.coarsePointer && env.narrowViewport;
}

export interface InstallCopy {
  readonly title: string;
  readonly body: string;
  readonly iosHowTo: string | undefined;
  readonly androidHowTo: string | undefined;
  readonly nativeAction: string | undefined;
}

export function installCopy(platform: ClientPlatform, canNativeInstall: boolean): InstallCopy {
  const title = 'Recevez vos commandes sur l’écran d’accueil';
  if (platform === 'ios') {
    return {
      title,
      body: 'Sur iPhone, les notifications push n’arrivent que depuis l’application.',
      iosHowTo: 'Touchez Partager, puis « Sur l’écran d’accueil ».',
      androidHowTo: undefined,
      nativeAction: undefined,
    };
  }
  if (platform === 'android') {
    return {
      title,
      body: 'Pour les notifications, même lorsque le navigateur est fermé.',
      iosHowTo: undefined,
      androidHowTo: canNativeInstall
        ? undefined
        : 'Menu du navigateur, puis « Installer l’application ».',
      nativeAction: canNativeInstall ? 'Installer l’application' : undefined,
    };
  }
  return {
    title,
    body: "Pour les notifications push, installez Champ'Table sur l’écran d’accueil.",
    iosHowTo: 'iPhone : Partager, puis « Sur l’écran d’accueil ».',
    androidHowTo: 'Android : menu du navigateur, puis « Installer l’application ».',
    nativeAction: canNativeInstall ? 'Installer l’application' : undefined,
  };
}
